import type {
  MapClient,
  MapFeatureData,
  SearchHook,
  SearchHookLocation,
} from "@repo/maps";
import { isAbortError } from "@repo/maps/is-abort-error";

// TODO: improve location inference
export async function estimateLocationFromMap(
  mapClient: MapClient | null,
  { suggest, retrieve, fromCoords }: SearchHook,
): Promise<SearchHookLocation | null> {
  if (!mapClient) {
    return null;
  }

  const bbox = mapClient.getBounds() ?? undefined;
  const mapCenter = mapClient.getCoords();

  // Prefer reverse geocoding the map center to avoid "random first rendered label" issues.
  if (
    Number.isFinite(mapCenter.lat) &&
    Number.isFinite(mapCenter.lng)
  ) {
    try {
      const reverse = await fromCoords(mapCenter);
      if (reverse.locations.length > 0) {
        const bestReverseLocation =
          reverse.locations.find(
            (location) =>
              !!location.context.place ||
              !!location.context.district ||
              !!location.context.region,
          ) ?? reverse.locations[0];

        if (bestReverseLocation) {
          return bestReverseLocation;
        }
      }
    } catch (error) {
      if (isAbortError(error)) {
        return null;
      }
      // Fall back to rendered feature inference.
    }
  }

  let maxRetries = 50;

  // Fallback for cases where reverse lookup is unavailable.
  async function queryRenderedFeatures() {
    return new Promise<MapFeatureData[]>((resolve) => {
      const intervalId = setInterval(() => {
        maxRetries--;

        if (!mapClient) {
          if (maxRetries <= 0) {
            clearInterval(intervalId);
            resolve([]);
          }
          return;
        }

        try {
          const features = mapClient.queryRenderedFeatures();
          if (features.length > 0 || maxRetries <= 0) {
            clearInterval(intervalId);
            resolve(features);
          }
        } catch {
          if (maxRetries <= 0) {
            clearInterval(intervalId);
            resolve([]);
          }
        }
      }, 50);
    });
  }

  const features = await queryRenderedFeatures();

  if (!bbox || features.length === 0) {
    return null;
  }

  const firstFeature = features[0];
  if (!firstFeature.name) {
    return null;
  }

  const { suggestions } = await suggest(firstFeature.name, {
    bbox,
  });

  if (suggestions.length === 0) {
    return null;
  }

  const { locations } = await retrieve(suggestions[0]);
  if (locations.length === 0) {
    return null;
  }

  return locations[0];
}

const MAX_LOCATION_CACHE_SIZE = 200;

export function gridPrecisionForZoom(zoom: number): number {
  if (zoom >= 14) return 3; // ~110m cells
  if (zoom >= 10) return 2; // ~1.1km cells
  if (zoom >= 6) return 1;  // ~11km cells
  return 0;                  // ~111km cells
}

export function roundCoord(value: number, precision: number): number {
  const factor = 10 ** precision;
  return Math.round(value * factor) / factor;
}

export async function cachedEstimateLocationFromMap(
  mapClient: MapClient | null,
  searchHook: SearchHook,
  zoom: number,
  cache: Map<string, SearchHookLocation>,
): Promise<SearchHookLocation | null> {
  if (!mapClient) return null;

  const center = mapClient.getCoords();
  if (!Number.isFinite(center.lat) || !Number.isFinite(center.lng)) {
    return estimateLocationFromMap(mapClient, searchHook);
  }

  const precision = gridPrecisionForZoom(zoom);
  const key = `${roundCoord(center.lat, precision)},${roundCoord(center.lng, precision)}`;

  const cached = cache.get(key);
  if (cached) return cached;

  const result = await estimateLocationFromMap(mapClient, searchHook);
  if (!result) return null;

  if (cache.size >= MAX_LOCATION_CACHE_SIZE) {
    const oldest = cache.keys().next().value;
    if (oldest !== undefined) cache.delete(oldest);
  }

  cache.set(key, result);
  return result;
}
